import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, RotateCw, Settings, Trash2, MessageSquare } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { toast } from "sonner";

interface AiRegenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegenerateWithPrevious: () => void;
  onRegenerateWithNew: () => void;
  onClear: () => void;
  onRegenerateWithInstructions: (instructions: string) => void;
}

export function AiRegenerateDialog({
  open,
  onOpenChange,
  onRegenerateWithPrevious,
  onRegenerateWithNew,
  onClear,
  onRegenerateWithInstructions,
}: AiRegenerateDialogProps) {
  const [instructions, setInstructions] = useState("");
  const [showInstructionsInput, setShowInstructionsInput] = useState(false);

  const handleRegenerateWithInstructions = () => {
    if (instructions.trim()) {
      onRegenerateWithInstructions(instructions);
      setInstructions("");
      setShowInstructionsInput(false);
      onOpenChange(false);
    }
  };

  const handleClearWithConfirmation = () => {
    if (
      confirm(
        "Are you sure you want to clear the entire AI-generated schedule? This action cannot be undone.",
      )
    ) {
      onClear();
      toast.success("AI-generated schedule cleared");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Re-generate with AI
          </DialogTitle>
          <DialogDescription>Choose how you want to regenerate the crew schedule</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {!showInstructionsInput ? (
            <>
              <Button
                variant="outline"
                className="h-auto w-full justify-start gap-3 py-4"
                onClick={() => {
                  onRegenerateWithPrevious();
                }}
              >
                <RotateCw className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-semibold">Re-generate with previous settings</div>
                  <div className="text-xs text-muted-foreground">
                    Review and confirm before generating
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto w-full justify-start gap-3 py-4"
                onClick={() => {
                  onRegenerateWithNew();
                  onOpenChange(false);
                }}
              >
                <Settings className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-semibold">Re-generate with new settings</div>
                  <div className="text-xs text-muted-foreground">
                    Adjust configuration before generating
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto w-full justify-start gap-3 py-4"
                onClick={() => setShowInstructionsInput(true)}
              >
                <MessageSquare className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-semibold">Re-generate with instructions</div>
                  <div className="text-xs text-muted-foreground">
                    Provide specific instructions for changes
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto w-full justify-start gap-3 border-destructive/50 py-4 hover:bg-destructive/10"
                onClick={handleClearWithConfirmation}
              >
                <Trash2 className="h-5 w-5 text-destructive" />
                <div className="text-left">
                  <div className="font-semibold text-destructive">Clear generated schedule</div>
                  <div className="text-xs text-muted-foreground">
                    Remove all AI-generated assignments
                  </div>
                </div>
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Instructions for AI</Label>
                <Textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="E.g., 'Move all breaks to later in the day' or 'Give crew member John more assignments on Main Stage'"
                  rows={4}
                  className="resize-none"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowInstructionsInput(false);
                    setInstructions("");
                  }}
                >
                  Back
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleRegenerateWithInstructions}
                  disabled={!instructions.trim()}
                >
                  Re-generate
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
